import React, { useEffect, useState } from 'react';
import {
    Anchor,
    Box,
    DataTable,
    Heading,
    Page,
    PageContent,
    Pagination,
} from 'grommet';
import PropTypes from 'prop-types';
import QueryString from 'qs';
import { API } from '../../api';

export const Alumni = ({
    schoolId,
    studentPathTemplate = '/contacts/:id',
    backHref,
    backLabel,
}) => {
    const stepOptions = [25, 50, 100];
    const [students, setStudents] = useState([]);
    const [page, setPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [pageSize, setPageSize] = useState(stepOptions[0]);

    useEffect(() => {
        if (!schoolId || !page) return;
        let isActive = true;

        (async () => {
            const response = await loadAlumni({
                schoolId,
                page,
                pageSize,
            });

            if (isActive && response) {
                setStudents(response.data);
                setTotalItems(response.meta.pagination.total);
            }
        })();

        return () => {
            isActive = false;
        };
    }, [schoolId, page, pageSize]);

    const handlePageChange = ({ page, startIndex, endIndex }) => {
        const newPageSize = endIndex - startIndex;
        if (pageSize !== newPageSize) {
            setPageSize(newPageSize);
            setPage(1);
            return;
        }
        setPage(page);
    };

    const dataColumns = [
        {
            header: 'Name',
            property: 'attributes.lastName',
            render: (contact) => (
                <Anchor
                    key={contact.id}
                    href={studentPathTemplate.replace(':id', contact.id)}
                    label={`${contact.attributes.firstName} ${contact.attributes.lastName}`}
                />
            ),
            sortable: true,
        },
        {
            header: 'Email',
            property: 'attributes.email',
            sortable: true,
        },
        {
            header: 'Graduation Semester',
            property: 'attributes.graduationSemester',
            render: (contact) => {
                const semester = contact.attributes.graduationSemester;
                if (!semester) return '';
                return semester[0].toUpperCase() + semester.slice(1);
            },
            sortable: true,
        },
        {
            header: 'Graduation Year',
            property: 'attributes.graduationYear',
            sortable: true,
        },
    ];

    return (
        <Page
            kind="full"
            pad={{
                top: '30px',
                horizontal: 'medium',
                bottom: '30px',
            }}>
            <PageContent gap="medium">
                <Box fill="horizontal">
                    <Box
                        direction="row-responsive"
                        justify="between"
                        align="center">
                        <Heading level="3">Alumni</Heading>
                        {backHref && (
                            <Anchor
                                href={backHref}
                                label={`← Back to ${backLabel || 'School'}`}
                            />
                        )}
                    </Box>
                    <DataTable
                        primaryKey="id"
                        sortable
                        border="bottom"
                        data={students}
                        columns={dataColumns}
                    />
                    <Box align="center" justify="center" pad="small">
                        <Pagination
                            numberItems={totalItems}
                            onChange={({ page, startIndex, endIndex }) =>
                                handlePageChange({
                                    page,
                                    startIndex,
                                    endIndex,
                                })
                            }
                            step={pageSize}
                            stepOptions={stepOptions}
                            summary={true}
                        />
                    </Box>
                </Box>
            </PageContent>
        </Page>
    );
};

Alumni.propTypes = {
    schoolId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    studentPathTemplate: PropTypes.string,
    backHref: PropTypes.string,
    backLabel: PropTypes.string,
};

const loadAlumni = async ({ schoolId, page, pageSize } = {}) => {
    const query = QueryString.stringify({
        filters: {
            school: schoolId,
            enrichProgramStatus: 'alumni',
        },
        pagination: page && pageSize ? { page, pageSize } : undefined,
        sort: ['updatedAt:desc'],
    });

    try {
        const response = await API.get(`/api/contacts?${query}`);
        return response.data;
    } catch (error) {
        console.log(error);
        return null;
    }
};
